/**
 * 解析URL参数
 */
export function parseUrlParams(
  url: string = window.location.href
): Record<string, string> {
  const params: Record<string, string> = {};
  const urlObj = new URL(url);

  urlObj.searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
}

/**
 * 构建URL参数字符串
 */
export function buildUrlParams(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  return searchParams.toString();
}

/**
 * 添加URL参数
 */
export function addUrlParams(url: string, params: Record<string, any>): string {
  const urlObj = new URL(url, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      urlObj.searchParams.set(key, String(value));
    }
  });

  return urlObj.toString();
}

/**
 * 移除URL参数
 */
export function removeUrlParams(url: string, keys: string[]): string {
  const urlObj = new URL(url, window.location.origin);

  keys.forEach(key => {
    urlObj.searchParams.delete(key);
  });

  return urlObj.toString();
}

/**
 * 获取URL参数值
 */
export function getUrlParam(
  key: string,
  url: string = window.location.href
): string | null {
  const urlObj = new URL(url);
  return urlObj.searchParams.get(key);
}

/**
 * 检查是否为绝对URL
 */
export function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//.test(url);
}

/**
 * 获取域名
 */
export function getDomain(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

/**
 * 获取文件扩展名
 */
export function getFileExtension(url: string): string {
  const pathname = new URL(url, window.location.origin).pathname;
  const lastDot = pathname.lastIndexOf('.');
  return lastDot > 0 ? pathname.slice(lastDot + 1).toLowerCase() : '';
}

/**
 * 获取文件名
 */
export function getFileName(url: string): string {
  const pathname = new URL(url, window.location.origin).pathname;
  return pathname.split('/').pop() || '';
}

/**
 * 连接URL路径
 */
export function joinUrl(...parts: string[]): string {
  return parts
    .map((part, index) => {
      if (index === 0) {
        return part.replace(/\/+$/, '');
      }
      return part.replace(/^\/+|\/+$/g, '');
    })
    .filter(Boolean)
    .join('/');
}

/**
 * 处理静态文件路径
 * 开发环境下去掉应用前缀，生产环境下保留完整路径
 * @param path 原始路径，例如：/template-app/static-files/1.txt
 * @param appName 应用名称，例如：template-app
 * @param isDev 是否为开发环境，默认根据 NODE_ENV 判断
 * @returns 处理后的路径
 */
export function resolveStaticPath(
  path: string,
  appName: string,
  isDev: boolean = process.env.NODE_ENV === 'development'
): string {
  if (!path || !appName) {
    return path;
  }

  // 确保路径以 / 开头
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // 构建应用前缀
  const appPrefix = `/${appName}`;

  if (isDev) {
    // 开发环境：去掉应用前缀
    if (normalizedPath.startsWith(appPrefix)) {
      return normalizedPath.slice(appPrefix.length) || '/';
    }
    return normalizedPath;
  } else {
    // 生产环境：保留完整路径
    return normalizedPath;
  }
}
